import { Button, Group, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { businessToday, type DataBankEntryDto } from '@mexp/shared';
import { http } from '@/api/client';
import { toBusinessDate } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';

/** RA records or corrects a registration certificate's issue and expiry dates (both, or neither). */
export function ValidityModal({ entry, onClose }: { entry: DataBankEntryDto | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [issueDate, setIssueDate] = useState<string | null>(null);
  const [expiryDate, setExpiryDate] = useState<string | null>(null);

  useEffect(() => {
    setIssueDate(entry?.issueDate ?? null);
    setExpiryDate(entry?.expiryDate ?? null);
  }, [entry]);

  const today = businessToday();
  const error =
    !issueDate !== !expiryDate
      ? 'Enter both dates, or clear both'
      : issueDate && issueDate > today
        ? 'The issue date cannot be in the future'
        : issueDate && expiryDate && expiryDate <= issueDate
          ? 'The expiry date must be after the issue date'
          : null;

  const save = useMutation({
    mutationFn: () => http.patch<DataBankEntryDto>(`/data-bank/entries/${entry!.id}/validity`, { issueDate, expiryDate }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['data-bank'] });
      notifySuccess('Dates saved');
      onClose();
    },
    onError: (e) => notifyError(e, 'The dates were not saved'),
  });

  return (
    <Modal opened={!!entry} onClose={onClose} title="Registration certificate dates">
      {entry && (
        <Stack gap="md">
          <Stack gap={2}>
            <Text fw={600} fz="sm">
              {entry.name}
            </Text>
            <Text fz="xs" c="dimmed">
              {[entry.party, entry.country].filter(Boolean).join(' · ')}
            </Text>
          </Stack>
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <DateInput label="Issue date" valueFormat="DD MMM YYYY" clearable maxDate={today} value={issueDate} onChange={(v) => setIssueDate(toBusinessDate(v))} />
            <DateInput
              label="Expiry date"
              valueFormat="DD MMM YYYY"
              clearable
              minDate={issueDate ?? undefined}
              value={expiryDate}
              onChange={(v) => setExpiryDate(toBusinessDate(v))}
              error={error}
            />
          </SimpleGrid>
          <Text fz="xs" c="dimmed">
            The client and RA are reminded 90 days before the expiry date and on it.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!!error}>
              Save dates
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
